import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users,
  UserCheck,
  Wallet,
  TrendingUp,
  Clock,
  Briefcase,
  BarChart3,
  Award,
  Plane,
  AlertCircle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  PieChartIcon,
  LineChart,
  Activity,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart as RechartsLineChart,
  Line,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import api from '../lib/api';

// Custom colors for charts
const COLORS = {
  primary: '#3B82F6',
  secondary: '#8B5CF6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#06B6D4',
  pink: '#EC4899',
  indigo: '#6366F1',
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1'];

const HRDashboardPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    employees: { total: 0, active: 0, inactive: 0, new_this_month: 0 },
    payroll: { current_month: 0, previous_month: 0, ytd_total: 0, avg_salary: 0, employer_charges: 0 },
    absences: { pending: 0, approved: 0, total_days: 0, vacation_days_used: 0 },
    contracts: { cdi: 0, cdd: 0, other: 0 },
    departments: [],
    monthly_costs: [],
    performance: {
      retention: 0,
      attendance: 0,
      productivity: 0,
      satisfaction: 0,
      training: 0,
      engagement: 0,
      turnover: 0,
      cost_per_employee: 0
    }
  });

  const years = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - 1 + i);

  useEffect(() => {
    loadDashboardData();
  }, [selectedYear]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hr/dashboard', { params: { year: selectedYear } });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      // Fallback mock data for demo
      setStats({
        employees: { total: 12, active: 10, inactive: 2, new_this_month: 2 },
        payroll: { 
          current_month: 85000, 
          previous_month: 82000, 
          ytd_total: 680000, 
          avg_salary: 8500,
          employer_charges: 12500
        },
        absences: { pending: 3, approved: 15, total_days: 45, vacation_days_used: 38 },
        contracts: { cdi: 8, cdd: 3, other: 1 },
        departments: [
          { name: 'IT', count: 5, percentage: 41.7 },
          { name: 'Finance', count: 3, percentage: 25 },
          { name: 'Marketing', count: 2, percentage: 16.7 },
          { name: 'RH', count: 2, percentage: 16.7 }
        ],
        monthly_costs: [
          { month: 'Jan', gross: 78000, net: 62000, charges: 11500 },
          { month: 'Fév', gross: 80000, net: 64000, charges: 11800 },
          { month: 'Mar', gross: 82000, net: 65500, charges: 12100 },
          { month: 'Avr', gross: 81000, net: 64800, charges: 11950 },
          { month: 'Mai', gross: 83000, net: 66400, charges: 12250 },
          { month: 'Jun', gross: 85000, net: 68000, charges: 12500 },
          { month: 'Jul', gross: 84000, net: 67200, charges: 12400 },
          { month: 'Aoû', gross: 82000, net: 65600, charges: 12100 },
          { month: 'Sep', gross: 86000, net: 68800, charges: 12700 },
          { month: 'Oct', gross: 85000, net: 68000, charges: 12500 },
          { month: 'Nov', gross: 87000, net: 69600, charges: 12850 },
          { month: 'Déc', gross: 90000, net: 72000, charges: 13300 },
        ],
        performance: {
          retention: 92,
          attendance: 96,
          productivity: 85,
          satisfaction: 78,
          training: 70,
          engagement: 82,
          turnover: 8,
          cost_per_employee: 8500
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { 
      style: 'currency', 
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getChangeIndicator = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    const isPositive = change > 0;
    return (
      <div className={`flex items-center text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        <span>{Math.abs(change).toFixed(1)}%</span>
      </div>
    );
  };

  // Prepare chart data
  const contractData = [
    { name: 'CDI', value: stats.contracts.cdi, color: COLORS.primary },
    { name: 'CDD', value: stats.contracts.cdd, color: COLORS.warning },
    { name: 'Autres', value: stats.contracts.other, color: COLORS.secondary },
  ].filter(d => d.value > 0);

  const departmentData = stats.departments.map((dept, idx) => ({
    name: dept.name || 'Non défini',
    value: dept.count,
    color: PIE_COLORS[idx % PIE_COLORS.length],
  }));

  // Monthly cost breakdown for stacked bars
  const monthlyCostBreakdown = stats.monthly_costs.map(m => ({
    month: m.month,
    'Salaires nets': m.net,
    'Charges patronales': m.charges,
    'Déductions employés': m.gross - m.net - (m.charges || 0),
  }));

  // Employee evolution data (simulated based on monthly costs)
  const employeeEvolution = stats.monthly_costs.map((m, idx) => ({
    month: m.month,
    effectifs: Math.max(1, stats.employees.total - Math.floor(Math.random() * 2) + (idx > 6 ? 1 : 0)),
    nouveaux: idx % 3 === 0 ? 1 : 0,
    départs: idx % 5 === 0 ? 1 : 0,
  }));

  // HR Performance radar data
  // HR Performance data from API (dynamic)
  const hrPerformanceData = [
    { metric: 'Rétention', value: stats.performance?.retention || 0, fullMark: 100 },
    { metric: 'Présence', value: stats.performance?.attendance || 0, fullMark: 100 },
    { metric: 'Productivité', value: stats.performance?.productivity || 0, fullMark: 100 },
    { metric: 'Satisfaction', value: stats.performance?.satisfaction || 0, fullMark: 100 },
    { metric: 'Formation', value: stats.performance?.training || 0, fullMark: 100 },
    { metric: 'Engagement', value: stats.performance?.engagement || 0, fullMark: 100 },
  ];

  // Top salaries by department
  const topSalariesByDept = stats.departments.map(dept => ({
    name: dept.name || 'Non défini',
    salaire: Math.round((stats.payroll.avg_salary || 8500) * (0.8 + Math.random() * 0.4)),
    effectifs: dept.count,
  })).sort((a, b) => b.salaire - a.salaire);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-medium text-slate-900">{label}</p>
          {payload.map((entry) => (
            <p key={entry.dataKey || entry.name} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hr-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            {t('hr.dashboard') || 'Dashboard RH'}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('hr.dashboardSubtitle') || 'Vue d\'ensemble des ressources humaines'}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select 
            value={selectedYear.toString()} 
            onValueChange={(v) => setSelectedYear(parseInt(v))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadDashboardData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Employees */}
        <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.totalEmployees') || 'Employés'}</p>
                <p className="text-3xl font-bold text-slate-900">{stats.employees.total}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <UserCheck className="w-3 h-3 mr-1" />
                    {stats.employees.active} actifs
                  </Badge>
                  {stats.employees.new_this_month > 0 && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      +{stats.employees.new_this_month}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Payroll */}
        <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.monthlyPayroll') || 'Masse salariale'}</p>
                <p className="text-3xl font-bold text-slate-900">{formatCurrency(stats.payroll.current_month)}</p>
                <div className="flex items-center gap-2 mt-1">
                  {getChangeIndicator(stats.payroll.current_month, stats.payroll.previous_month)}
                  <span className="text-xs text-slate-500">vs mois préc.</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Salary */}
        <Card className="border-l-4 border-l-purple-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.avgSalary') || 'Salaire moyen'}</p>
                <p className="text-3xl font-bold text-slate-900">{formatCurrency(stats.payroll.avg_salary)}</p>
                <p className="text-xs text-slate-500 mt-1">brut mensuel</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Absences */}
        <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t('hr.pendingAbsences') || 'Absences'}</p>
                <p className="text-3xl font-bold text-slate-900">{stats.absences.pending}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {stats.absences.approved} approuvées
                  </Badge>
                </div>
              </div>
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different chart views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Vue globale
          </TabsTrigger>
          <TabsTrigger value="costs" className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            Coûts
          </TabsTrigger>
          <TabsTrigger value="evolution" className="flex items-center gap-2">
            <LineChart className="w-4 h-4" />
            Évolution
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Performance
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Contract Types Pie Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-blue-600" />
                  Répartition par type de contrat
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={contractData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {contractData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Department Distribution Pie Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                  Répartition par département
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={departmentData}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {departmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* YTD Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Récapitulatif annuel {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                  <p className="text-sm text-slate-600 mb-1">Total brut versé</p>
                  <p className="text-2xl font-bold text-blue-700">{formatCurrency(stats.payroll.ytd_total)}</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                  <p className="text-sm text-slate-600 mb-1">Charges patronales</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatCurrency(stats.payroll.employer_charges * 12)}
                  </p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                  <p className="text-sm text-slate-600 mb-1">Coût total employeur</p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatCurrency(stats.payroll.ytd_total + (stats.payroll.employer_charges * 12))}
                  </p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                  <p className="text-sm text-slate-600 mb-1">Jours de congés pris</p>
                  <p className="text-2xl font-bold text-purple-700">{stats.absences.vacation_days_used}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs" className="space-y-4">
          {/* Stacked Bar Chart - Cost Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-600" />
                Décomposition des coûts mensuels
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyCostBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="Salaires nets" stackId="a" fill={COLORS.success} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Charges patronales" stackId="a" fill={COLORS.warning} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Déductions employés" stackId="a" fill={COLORS.info} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Horizontal Bar - Top Salaries by Department */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-600" />
                Salaire moyen par département
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topSalariesByDept} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      type="number" 
                      tickFormatter={(value) => formatCurrency(value)}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="salaire" fill={COLORS.indigo} radius={[0, 4, 4, 0]} name="Salaire moyen" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evolution Tab */}
        <TabsContent value="evolution" className="space-y-4">
          {/* Line Chart - Payroll Evolution */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Évolution de la masse salariale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart data={stats.monthly_costs}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="gross" 
                      stroke={COLORS.primary} 
                      strokeWidth={3}
                      dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                      name="Salaire brut"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="net" 
                      stroke={COLORS.success} 
                      strokeWidth={3}
                      dot={{ fill: COLORS.success, strokeWidth: 2 }}
                      name="Salaire net"
                    />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Area Chart - Net vs Charges */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Coûts nets vs Charges patronales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.monthly_costs}>
                    <defs>
                      <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorCharges" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.warning} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={COLORS.warning} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="net" 
                      stroke={COLORS.success} 
                      fillOpacity={1} 
                      fill="url(#colorNet)"
                      name="Salaires nets"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="charges" 
                      stroke={COLORS.warning} 
                      fillOpacity={1} 
                      fill="url(#colorCharges)"
                      name="Charges patronales"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Employee Evolution Line Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Évolution des effectifs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart data={employeeEvolution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="effectifs" 
                      stroke={COLORS.primary} 
                      strokeWidth={3}
                      dot={{ fill: COLORS.primary, strokeWidth: 2, r: 5 }}
                      name="Effectifs totaux"
                    />
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Radar Chart - HR Performance */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-pink-600" />
                  Performance RH
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={hrPerformanceData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                      <Radar
                        name="Performance"
                        dataKey="value"
                        stroke={COLORS.pink}
                        fill={COLORS.pink}
                        fillOpacity={0.5}
                        strokeWidth={2}
                      />
                      <Tooltip />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* KPI Cards */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Indicateurs clés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                    <p className="text-sm text-slate-600">Taux de rétention</p>
                    <p className="text-3xl font-bold text-green-700">{stats.performance?.retention || 0}%</p>
                    <p className="text-xs text-green-600 mt-1">
                      {stats.performance?.retention >= 90 ? '✓ Excellent' : stats.performance?.retention >= 75 ? '→ À surveiller' : '⚠ Critique'}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                    <p className="text-sm text-slate-600">Taux de présence</p>
                    <p className="text-3xl font-bold text-blue-700">{stats.performance?.attendance || 0}%</p>
                    <p className="text-xs text-blue-600 mt-1">
                      {stats.performance?.attendance >= 95 ? '✓ Excellent' : stats.performance?.attendance >= 85 ? '→ Normal' : '⚠ À améliorer'}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                    <p className="text-sm text-slate-600">Turnover</p>
                    <p className="text-3xl font-bold text-purple-700">{stats.performance?.turnover || 0}%</p>
                    <p className="text-xs text-purple-600 mt-1">
                      {stats.performance?.turnover <= 5 ? '✓ Faible' : stats.performance?.turnover <= 15 ? '→ Normal' : '⚠ Élevé'}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                    <p className="text-sm text-slate-600">Coût par employé</p>
                    <p className="text-3xl font-bold text-orange-700">
                      {formatCurrency(stats.performance?.cost_per_employee || 0)}
                    </p>
                    <p className="text-xs text-orange-600 mt-1">mensuel moyen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Quick Actions & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t('hr.quickActions') || 'Actions rapides'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300 transition-colors" 
                onClick={() => window.location.href = '/app/employees'}
              >
                <Users className="w-5 h-5 text-blue-600" />
                <span className="text-sm">Nouvel employé</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300 transition-colors" 
                onClick={() => window.location.href = '/app/payslips'}
              >
                <Wallet className="w-5 h-5 text-green-600" />
                <span className="text-sm">Créer fiche paie</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-orange-50 hover:border-orange-300 transition-colors" 
                onClick={() => window.location.href = '/app/absences'}
              >
                <Plane className="w-5 h-5 text-orange-600" />
                <span className="text-sm">Gérer absences</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300 transition-colors" 
                onClick={() => window.location.href = '/app/accounting'}
              >
                <BarChart3 className="w-5 h-5 text-purple-600" />
                <span className="text-sm">Écritures compta</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alerts & Reminders */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              {t('hr.alertsReminders') || 'Alertes & Rappels'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.absences.pending > 0 && (
                <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800">
                      {stats.absences.pending} demande(s) d'absence en attente
                    </p>
                    <p className="text-xs text-yellow-600">À traiter rapidement</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-yellow-700 border-yellow-300 hover:bg-yellow-200" 
                    onClick={() => window.location.href = '/app/absences'}
                  >
                    Voir
                  </Button>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
                <Wallet className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">
                    Fiches de paie du mois
                  </p>
                  <p className="text-xs text-blue-600">À générer avant fin du mois</p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-blue-700 border-blue-300 hover:bg-blue-200" 
                  onClick={() => window.location.href = '/app/payslips'}
                >
                  Créer
                </Button>
              </div>

              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
                <Award className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">
                    Certificats de salaire
                  </p>
                  <p className="text-xs text-green-600">Disponibles pour {selectedYear}</p>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-green-700 border-green-300 hover:bg-green-200" 
                  onClick={() => window.location.href = '/app/employees'}
                >
                  Générer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HRDashboardPage;
